import { get as getMetadata, ref as metadataRef } from 'firebase/database'
import {
  endBefore,
  onValue,
  orderByChild,
  query,
  ref,
  startAt,
  type Unsubscribe,
} from '../../services/dashboard-read-view'

import type { DashboardUser } from '../auth/auth-adapter'
import { getEnvironment } from '../../lib/environment'
import { createApiClient } from '../../services/api/client'
import { getFirebaseServices } from '../../services/firebase'
import {
  conversationAttentionPath,
  conversationAttentionSchema,
  type ConversationAttention,
} from '../shared/conversation-attention'

export type OverviewRepository = {
  getClinicInfo: (tenantId: string) => Promise<unknown>
  getClinicTimeZone: (tenantId: string) => Promise<unknown>
  getAttention: (tenantId: string) => Promise<ConversationAttention>
  subscribeOverview: (
    tenantId: string,
    handleValue: (value: unknown) => void,
    handleError: (error: unknown) => void
  ) => Unsubscribe
  subscribeTodayAppointments: (
    tenantId: string,
    startAtMs: number,
    endBeforeMs: number,
    handleValue: (value: unknown) => void,
    handleError: (error: unknown) => void
  ) => Unsubscribe
  subscribeTodayAppointmentCoverage: (
    tenantId: string,
    handleValue: (value: unknown) => void,
    handleError: (error: unknown) => void
  ) => Unsubscribe
}

function databaseRef(path: string) {
  return ref(getFirebaseServices().database, path)
}

export function createOverviewRepository({
  getUser,
  baseUrl = getEnvironment().userManagementBaseUrl,
  fetchImplementation,
}: {
  getUser: () => DashboardUser | null
  baseUrl?: string
  fetchImplementation?: typeof fetch
}): OverviewRepository {
  const api = createApiClient({ baseUrl, getUser, fetchImplementation })
  return {
    async getClinicInfo(tenantId) {
      const snapshot = await getMetadata(
        metadataRef(
          getFirebaseServices().database,
          `clinic/${tenantId}/settings/clinicInfo`
        )
      )
      const value: unknown = snapshot.exists() ? snapshot.val() : null
      return value
    },

    async getClinicTimeZone(tenantId) {
      const snapshot = await getMetadata(
        metadataRef(
          getFirebaseServices().database,
          `clinic/${tenantId}/settings/timeZone`
        )
      )
      const value: unknown = snapshot.exists() ? snapshot.val() : null
      return value
    },

    getAttention(tenantId) {
      return api.request(conversationAttentionPath(tenantId), {
        method: 'GET',
        responseSchema: conversationAttentionSchema,
      })
    },

    subscribeOverview(tenantId, handleValue, handleError) {
      return onValue(
        databaseRef(`dashboardOverview/${tenantId}/v1`),
        (snapshot) => handleValue(snapshot.exists() ? snapshot.val() : null),
        handleError
      )
    },

    subscribeTodayAppointments(
      tenantId,
      startAtMs,
      endBeforeMs,
      handleValue,
      handleError
    ) {
      // The exact clinic-day window form (no page limit): a high-volume day
      // is read whole and checked against its coverage count.
      const appointmentsQuery = query(
        databaseRef(`dashboardAppointments/${tenantId}`),
        orderByChild('scheduledAtMs'),
        startAt(startAtMs),
        endBefore(endBeforeMs)
      )
      return onValue(
        appointmentsQuery,
        (snapshot) => handleValue(snapshot.exists() ? snapshot.val() : null),
        handleError
      )
    },

    subscribeTodayAppointmentCoverage(tenantId, handleValue, handleError) {
      return onValue(
        databaseRef(`dashboardAppointmentCoverage/v1/${tenantId}`),
        (snapshot) => handleValue(snapshot.exists() ? snapshot.val() : null),
        handleError
      )
    },
  }
}

export const firebaseOverviewRepository = createOverviewRepository({
  getUser: () => getFirebaseServices().auth.currentUser,
})

export function isPermissionDenied(error: unknown) {
  if (typeof error !== 'object' || error === null || !('code' in error)) {
    return false
  }

  const code = String(error.code).toLowerCase()
  return (
    code.includes('permission-denied') || code.includes('permission_denied')
  )
}
